import { EditorProps } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import {
  EditorV3,
  EditorV3Align,
  EditorV3Styles,
  getObjectDiff,
  getTextFromEditorV3,
  IEditorV3,
} from "editor-v3";
import { eHierarchyLevel } from "functions/api/enums";
import { CommentableEntityType } from "functions/api/notifications/interface";
import { eStyleTlfType } from "functions/api/styles/getAllStyles";
import { omitDeep } from "functions/omitDeep";
import { isEqual } from "lodash";
import { useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { CommentScope } from "./comments/CommentScope";
import { stylesToV3 } from "./stylesToV3";

/**
 * Configuration for comment functionality in EditorV3Wrapper.
 */
export interface CommentConfig {
  /** Unique identifier for the comment scope */
  id: string;
  /** Active context type for comments */
  activeContext: CommentableEntityType;
  /** Label text for the comment action in context menu */
  label: string;
  /** When true, commenting is disabled */
  disabled?: boolean;
  /** Position configuration for the comment icon */
  iconPosition?: { top?: string; right?: string; bottom?: string; left?: string };
  /** When true, hides the comment icon */
  noIcon?: boolean;
}

/**
 * Wrapper around EditorV3 that handles pre-replaced text display logic and optional comment functionality.
 */
interface EditorV3WrapperProps extends EditorProps<IEditorV3> {
  customStyleMap?: EditorV3Styles;
  debounceMilliseconds?: number;
  spellCheck?: boolean;
  allowNewLine?: boolean;
  noBorder?: boolean;
  allowWindowView?: boolean;
  allowMarkdown?: boolean;
  resize?: boolean | "vertical";
  preReplacedText?: IEditorV3 | null;
  /** Optional comment configuration. When provided, wraps the editor with CommentScope. */
  commentConfig?: CommentConfig;
}

/**
 * EditorV3Wrapper Component.
 * - Manages display of pre-replaced text when applicable
 * - Handles focus and mouse events to toggle between current and pre-replaced text
 * - Ensures only line differences are processed on input changes
 * - Adapts styling based on application settings and hierarchy context
 * - Optionally wraps editor with CommentScope for comment functionality
 *
 * @param props - Destructured parameter object
 * @param props.id - Unique identifier passed to the nested `EditorV3` instance.
 * @param props.value - The current `IEditorV3` value rendered by the editor.
 * @param props.setValue - Setter function to update the editor value state.
 * @param props.editable - When true, the editor allows user input and editing actions.
 * @param props.preReplacedText - Optional `IEditorV3` value shown as a temporary preview before replacement.
 * @param props.textAlignment - Preferred text alignment passed to the nested editor (mapped to `EditorV3Align`).
 * @param props.allowNewLine - Controls whether the editor allows newline insertion; defaults to `true`.
 * @param props.commentConfig - Optional configuration object for comment functionality.
 */
export const EditorV3Wrapper = ({
  id,
  value,
  setValue,
  editable,
  preReplacedText,
  textAlignment,
  allowNewLine,
  commentConfig,
  ...rest
}: EditorV3WrapperProps) => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  // Timestamp when pre-replaced text was shown (null means not showing)
  const [showPreReplacedText, setShowPreReplacedText] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Request animation frame ref
  const rafRef = useRef<number | null>(null);
  // Mouse and focus state ref
  const stateRef = useRef({ focused: false, mouse: { x: 0, y: 0 } });

  useEffect(() => {
    if (showPreReplacedText === null) return;
    /**
     * Tracks mouse position for pre-replacement text preview positioning.
     *
     * @param e - The mouse event containing cursor coordinates
     */
    const handleMouseMove = (e: MouseEvent) => {
      stateRef.current.mouse = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [showPreReplacedText]);

  /**
   * Memoized replacement text check
   */
  const replacement = useMemo(
    () =>
      editable &&
      preReplacedText &&
      !isEqual(getTextFromEditorV3(value), getTextFromEditorV3(preReplacedText)),
    [editable, preReplacedText, value],
  );

  /**
   * Comprehensive check function that verifies mouse position and focus
   */
  const performComprehensiveCheck = useEffectEvent(() => {
    // Look for table cell if there is one, otherwise use the wrapper
    const el = (wrapperRef.current?.closest("td") ?? wrapperRef.current) as HTMLElement | null;
    const EXTRA_MOUSE_COVER_PX = 8;
    const MIN_SHOW_TIME_MS = 500;

    /**
     * Performs the comprehensive check for hiding pre-replaced text
     */
    const check = (): void => {
      /* istanbul ignore else */
      if (el && replacement) {
        const hasFocus = el.contains(document.activeElement) || stateRef.current.focused;
        const rect = el.getBoundingClientRect();
        const { x, y } = stateRef.current.mouse;
        const mouseOver =
          x >= rect.left - EXTRA_MOUSE_COVER_PX &&
          x <= rect.right + EXTRA_MOUSE_COVER_PX &&
          y >= rect.top - EXTRA_MOUSE_COVER_PX &&
          y <= rect.bottom + EXTRA_MOUSE_COVER_PX;
        const now = Date.now();
        const elapsed =
          now -
          (showPreReplacedText ??
            // istanbul ignore next
            0);
        if (elapsed > MIN_SHOW_TIME_MS && !(hasFocus || mouseOver)) {
          // Only hide if at least 500ms has passed
          setShowPreReplacedText(null);
          rafRef.current = null;
          return;
        }
        rafRef.current = window.requestAnimationFrame(check);
      }
    };

    check();
  });

  /**
   * Stop comprehensive checking and clean up when pre-replaced text is hidden
   */
  useEffect(() => {
    if (showPreReplacedText !== null && replacement && rafRef.current === null) {
      rafRef.current = window.requestAnimationFrame(performComprehensiveCheck);
    }
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [showPreReplacedText, replacement]);

  /**
   * Handle mouse enter
   */
  const handleMouseEnter = () => {
    if (replacement && showPreReplacedText === null) {
      setShowPreReplacedText(Date.now());
    }
  };
  /**
   * Handle focus event
   */
  const handleFocus = () => {
    stateRef.current.focused = true;
    if (replacement && showPreReplacedText === null) {
      setShowPreReplacedText(Date.now());
    }
  };

  /**
   * Handle blur event
   */
  const handleBlur = () => {
    stateRef.current.focused = false;
  };

  /**
   * Determine custom style map based on hierarchy level
   */
  const thisCustomStyleMap: EditorV3Styles = stylesToV3(
    appSettings.styles,
    hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report
      ? eStyleTlfType.Shell
      : eStyleTlfType.Template,
  );

  /**
   * Determine if window view and markdown are allowed
   */
  const allowWindowViewMarkdown = useMemo(
    () => editable && hierarchyContext?.hierarchy?.level !== eHierarchyLevel.Report,
    [editable, hierarchyContext?.hierarchy?.level],
  );

  const editorElement = (
    <EditorV3
      {...rest}
      className={[
        rest.className ?? "",
        replacement && showPreReplacedText === null ? "has-replaced-text" : "",
      ]
        .filter((s) => s.trim() !== "")
        .join(" ")}
      id={id}
      input={showPreReplacedText !== null && preReplacedText ? preReplacedText : value}
      /**
       * Ensure that only line differences are returned/processed
       *
       * @param obj - Object containing `lines` property produced by the editor.
       */
      setObject={(obj) => {
        const ret = { lines: obj.lines };
        const diff = getObjectDiff(
          omitDeep(preReplacedText ?? value, ["isLocked"]),
          omitDeep(ret, ["isLocked"]),
        );
        if (diff) setValue(ret);
      }}
      editable={editable}
      customStyleMap={thisCustomStyleMap}
      textAlignment={textAlignment as EditorV3Align}
      spellCheck={false}
      debounceMilliseconds={-1}
      allowNewLine={allowNewLine ?? true}
      noBorder={true}
      allowWindowView={allowWindowViewMarkdown}
      allowMarkdown={allowWindowViewMarkdown}
      resize={false}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );

  return (
    <div
      ref={wrapperRef}
      className="editor-wrapper"
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {commentConfig ? (
        <CommentScope
          id={commentConfig.id}
          activeContext={commentConfig.activeContext}
          commentLabel={commentConfig.label}
          disabled={commentConfig.disabled}
          iconPosition={commentConfig.iconPosition}
          noIcon={commentConfig.noIcon}
        >
          {editorElement}
        </CommentScope>
      ) : (
        editorElement
      )}
    </div>
  );
};

EditorV3Wrapper.displayName = "EditorV3Wrapper";
